import type { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';
import logger from '@/util/logging/logger.js';
import type { ClientSummary, GroupSummary } from '@/types/shared';

const GROUP_CLIENTS_TTL_MS = 120_000;

interface ApiErrorResponseShape {
	message?: string;
	error?: string;
}

interface ApiErrorShape {
	response?: {
		status?: number;
		data?: ApiErrorResponseShape;
	};
}

export interface ClientAccessRecord {
	accessNumber: number | null;
	accessRole?: string | null;
	invitedEmail?: string | null;
	inviteeFirstName?: string | null;
	inviteeLastName?: string | null;
	status?: string | null;
	invitedAt?: string | null;
	acceptedAt?: string | null;
	user?:
		| {
				userNumber?: number | null;
				firstName?: string | null;
				lastName?: string | null;
				middleName?: string | null;
				photo?: string | null;
		  }
		| null
		| undefined;
}

export interface ClientRecord extends ClientSummary, Record<string, unknown> {
	clientNumber: number | null;
	name: string | null;
	shortName: string | null;
	group?: GroupSummary | null;
	groupId?: number | null;
	groupNumber?: number | null;
	clientAccessUser?:
		| {
				userNumber?: number | null;
				firstName?: string | null;
				lastName?: string | null;
				middleName?: string | null;
				photo?: string | null;
		  }
		| null
		| undefined;
	clientAccessStatus?: string | null;
	clientAccessInvitedEmail?: string | null;
	clientAccessInvitedAt?: string | null;
	clientAccessAcceptedAt?: string | null;
	clientAccesses?: ClientAccessRecord[];
}

interface ClientRecordInput extends Record<string, unknown> {
	clientNumber?: number | null;
	name?: string | null;
	shortName?: string | null;
	group?: GroupSummary | null;
	groupId?: GroupSummary | number | null;
	groupNumber?: number | null;
	clientAccessUser?: ClientRecord['clientAccessUser'];
	clientAccessStatus?: string | null;
	clientAccessInvitedEmail?: string | null;
	clientAccessInvitedAt?: string | null;
	clientAccessAcceptedAt?: string | null;
	clientAccesses?: unknown;
}

export interface InviteClientAccessPayload {
	email: string;
	firstName?: string | null;
	lastName?: string | null;
	accessRole?: string | null;
}

interface GroupClientsCacheEntry {
	data: ClientRecord[];
	ts: number;
}

interface FetchGroupClientsOptions {
	force?: boolean;
}

interface ClientStoreState {
	clients: ClientRecord[];
	groupClientsCache: Record<string, GroupClientsCacheEntry>;
}

interface StoreMutationError {
	status: number;
	data: {
		message: string;
	};
}

function normalizeClient(client: unknown): ClientRecord | null {
	if (!client || typeof client !== 'object') {
		return null;
	}

	const clientRecord = client as ClientRecordInput;
	const groupCandidate = typeof clientRecord.group === 'object' && clientRecord.group !== null ? clientRecord.group : typeof clientRecord.groupId === 'object' && clientRecord.groupId !== null ? clientRecord.groupId : null;
	const normalizedGroupId = groupCandidate?.groupNumber ?? (typeof clientRecord.groupId === 'number' ? clientRecord.groupId : null) ?? clientRecord.groupNumber ?? null;
	const clientAccesses = Array.isArray(clientRecord.clientAccesses) ? clientRecord.clientAccesses.map((access) => normalizeClientAccess(access)).filter((access): access is ClientAccessRecord => access != null) : [];

	return {
		...clientRecord,
		clientNumber: clientRecord.clientNumber ?? null,
		name: clientRecord.name ?? null,
		shortName: clientRecord.shortName ?? null,
		clientAccesses,
		groupId: normalizedGroupId,
		group: groupCandidate
	};
}

function normalizeClientAccess(access: unknown): ClientAccessRecord | null {
	if (!access || typeof access !== 'object') {
		return null;
	}

	const clientAccess = access as ClientAccessRecord;
	return {
		...clientAccess,
		accessNumber: clientAccess.accessNumber ?? null
	};
}

function normalizeClientList(payload: unknown): ClientRecord[] {
	return Array.isArray(payload) ? payload.map((client) => normalizeClient(client)).filter((client): client is ClientRecord => client != null) : [];
}

function replaceClientRecord(rows: ClientRecord[], client: ClientRecord): ClientRecord[] {
	return rows.map((row) => (Number(row.clientNumber) === Number(client.clientNumber) ? client : row));
}

function updateClientCaches(state: ClientStoreState, client: ClientRecord): void {
	const clientLoaded = state.clients.some((row) => Number(row.clientNumber) === Number(client.clientNumber));
	if (clientLoaded) {
		state.clients = replaceClientRecord(state.clients, client);
	}
	for (const [key, entry] of Object.entries(state.groupClientsCache)) {
		if (!entry.data.some((row) => Number(row.clientNumber) === Number(client.clientNumber))) {
			continue;
		}
		state.groupClientsCache[key] = {
			...entry,
			data: replaceClientRecord(entry.data, client),
			ts: Date.now()
		};
	}
	if (clientLoaded) {
		localStorage.setItem('clients', JSON.stringify(state.clients));
		localStorage.setItem('assignor_clients', JSON.stringify(state.clients));
	}
}

function parseStoredClients(value: string | null): ClientRecord[] {
	if (!value) {
		return [];
	}

	try {
		return normalizeClientList(JSON.parse(value) as unknown);
	} catch (error: unknown) {
		logger.warn('Failed to parse stored clients', { error });
		return [];
	}
}

function buildMutationError(error: unknown, fallback: string): StoreMutationError {
	const apiError = error as ApiErrorShape;
	return {
		status: apiError.response?.status ?? 500,
		data: {
			message: apiError.response?.data?.message ?? apiError.response?.data?.error ?? fallback
		}
	};
}

export const useClientStore = defineStore('clientStore', {
	state: (): ClientStoreState => ({
		clients: [],
		groupClientsCache: {}
	}),
	actions: {
		async fetchClient(clientNumber: number | string): Promise<ClientRecord | null | undefined> {
			if (shouldThrottle('fetchClients', 100)) {
				posthog.capture('Throttled_action', { action: 'fetchClient' });
				return;
			}

			const response = await apiClient.get(`/clients/id/${String(clientNumber)}`);
			if (response.status !== 200) {
				posthog.capture('client_fetch_unexpected_status', { status: response.status, clientNumber });
				return null;
			}

			return normalizeClient(response.data);
		},

		async fetchGroupsClients(groupId: number | string | null | undefined, options: FetchGroupClientsOptions = {}): Promise<ClientRecord[] | undefined> {
			const key = String(groupId);
			const cached = this.groupClientsCache[key];
			const now = Date.now();
			const force = options.force === true;

			if (!force && cached && now - cached.ts < GROUP_CLIENTS_TTL_MS) {
				this.clients = cached.data;
				return cached.data;
			}

			if (shouldThrottle('fetchGroupClients', 100)) {
				posthog.capture('Throttled_action', { action: 'fetchGroupsClients' });
				if (cached?.data) {
					this.clients = cached.data;
					return cached.data;
				}
				return;
			}

			if (!groupId) {
				posthog.capture('missing_parameter', { action: 'fetchGroupsClients', param: 'groupId' });
			}

			const response = await apiClient.get(`/clients/group/${String(groupId)}`);
			const formatted = normalizeClientList(response.data);
			this.groupClientsCache[key] = { data: formatted, ts: now };
			this.clients = formatted;
			localStorage.setItem('clients', JSON.stringify(formatted));
			localStorage.setItem('assignor_clients', JSON.stringify(formatted));
			return formatted;
		},

		async saveClient(client: Record<string, unknown>): Promise<AxiosResponse<unknown> | undefined> {
			if (shouldThrottle('saveClient', 100)) {
				posthog.capture('throttled_action', { action: 'saveClient' });
				return;
			}

			return await apiClient.post('/clients/save', client, {
				headers: {
					'content-type': 'application/merge-patch+json'
				}
			});
		},

		async updateClient(clientNumber: number | string, clientData: Record<string, unknown>): Promise<AxiosResponse<unknown> | StoreMutationError> {
			if (shouldThrottle('updateClient', 100)) {
				posthog.capture('throttled_action', { action: 'updateClient' });
				return { status: 429, data: { message: 'Too many requests' } };
			}

			try {
				return await apiClient.patch(`/clients/edit/${String(clientNumber)}`, clientData, {
					headers: {
						'content-type': 'application/merge-patch+json'
					}
				});
			} catch (error: unknown) {
				return buildMutationError(error, 'Failed to update client');
			}
		},

		async inviteClientAccess(groupNumber: number | string, clientNumber: number | string, payload: InviteClientAccessPayload): Promise<AxiosResponse<unknown> | StoreMutationError> {
			if (shouldThrottle('inviteClientAccess', 100)) {
				posthog.capture('throttled_action', { action: 'inviteClientAccess' });
				return { status: 429, data: { message: 'Too many requests' } };
			}

			try {
				const response = await apiClient.post(`/clients/group/${String(groupNumber)}/id/${String(clientNumber)}/client-access`, payload);
				const normalized = normalizeClient(response.data);
				if (normalized) {
					updateClientCaches(this, normalized);
					return { ...response, data: normalized };
				}
				return response;
			} catch (error: unknown) {
				return buildMutationError(error, 'Failed to send client access invite');
			}
		},

		async resendClientAccessInvite(groupNumber: number | string, clientNumber: number | string, accessNumber: number | string): Promise<AxiosResponse<unknown> | StoreMutationError> {
			if (shouldThrottle('resendClientAccessInvite', 100)) {
				posthog.capture('throttled_action', { action: 'resendClientAccessInvite' });
				return { status: 429, data: { message: 'Too many requests' } };
			}

			try {
				const response = await apiClient.post(`/clients/group/${String(groupNumber)}/id/${String(clientNumber)}/client-access/${String(accessNumber)}/resend`);
				const normalized = normalizeClient(response.data);
				if (normalized) {
					updateClientCaches(this, normalized);
					return { ...response, data: normalized };
				}
				return response;
			} catch (error: unknown) {
				return buildMutationError(error, 'Failed to resend client access invite');
			}
		},

		async removeClientAccess(groupNumber: number | string, clientNumber: number | string, accessNumber: number | string): Promise<AxiosResponse<unknown> | StoreMutationError> {
			if (shouldThrottle('removeClientAccess', 100)) {
				posthog.capture('throttled_action', { action: 'removeClientAccess' });
				return { status: 429, data: { message: 'Too many requests' } };
			}

			try {
				const response = await apiClient.delete(`/clients/group/${String(groupNumber)}/id/${String(clientNumber)}/client-access/${String(accessNumber)}`);
				const normalized = normalizeClient(response.data);
				if (normalized) {
					updateClientCaches(this, normalized);
					return { ...response, data: normalized };
				}
				return response;
			} catch (error: unknown) {
				return buildMutationError(error, 'Failed to remove client access');
			}
		},

		loadClientsFromStorage(): void {
			const storedClients = localStorage.getItem('clients') || localStorage.getItem('assignor_clients');
			this.clients = parseStoredClients(storedClients);
		}
	}
});
